import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';


import PublicNavigation from '../../components/ui/PublicNavigation';
import { clientAuthService } from '../../services/clientAuthService';
import { trackAuth, trackError } from '../../services/analyticsService';
import Icon from '../../components/AppIcon';


export default function ClientLogin() {
  const navigate = useNavigate();
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await clientAuthService?.login(emailOrPhone, password);
      
      if (result?.success) {
        // Track successful client login (skip in sandboxed environments)
        try {
          const isSandboxed = window.location?.hostname?.includes('builtwithrocket.new') || 
                              window.location?.hostname?.includes('rocket.new');
          if (!isSandboxed && typeof window.gtag !== 'undefined') {
            trackAuth('login', 'client');
          }
        } catch (analyticsError) {
          console.warn('Analytics tracking skipped:', analyticsError);
        }
        navigate('/client-dashboard');
      } else {
        trackError('auth_error', result?.error || 'Login failed', { user_type: 'client' });
        setError(result?.error || 'Erreur de connexion');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Erreur de connexion');
      trackError('auth_error', err?.message, { user_type: 'client' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Connexion Client - ADT Tracking</title>
        <meta name="description" content="Connectez-vous à votre espace client ADT Tracking" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <PublicNavigation />
        
        <div className="flex items-center justify-center px-3 sm:px-4 py-8 sm:py-12 lg:py-16">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-6 sm:mb-8">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="User" size={24} className="text-green-600" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Connexion Client</h1>
              <p className="text-sm sm:text-base text-gray-600">Accédez à votre espace personnel</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <p className="text-sm sm:text-base text-red-800">{error}</p>
              </div>
            )}

            {/* Login Form */}
            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email ou téléphone
                  </label>
                  <input
                    type="text"
                    value={emailOrPhone}
                    onChange={(e) => setEmailOrPhone(e?.target?.value)}
                    placeholder="email@example.com ou 0612345678"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder-gray-500 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Mot de passe
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    placeholder="Votre mot de passe"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder-gray-500 bg-white"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-sm min-h-[44px] text-sm sm:text-base font-medium"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Connexion...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="LogIn" size={18} />
                      <span>Se connecter</span>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                <Link
                  to="/client-forgot-password"
                  className="text-sm sm:text-base text-green-600 hover:text-green-700 active:text-green-800 font-medium inline-flex items-center space-x-1 min-h-[44px] py-2"
                >
                  <span>Mot de passe oublié ?</span>
                </Link>
                <Link
                  to="/client-password-reset"
                  className="text-sm text-gray-400 hover:text-gray-600 inline-flex items-center min-h-[44px] py-2"
                >
                  <span>Réinitialiser (admin)</span>
                </Link>
              </div>
            </div>

            {/* Registration Link */}
            <div className="mt-4 sm:mt-6 text-center">
              <p className="text-sm sm:text-base text-gray-600">
                Pas encore de compte ?{' '}
                <Link
                  to="/client-registration"
                  className="text-green-600 hover:text-green-700 active:text-green-800 font-medium"
                >
                  Créer un compte
                </Link>
              </p>
            </div>

            {/* Professional Client Link - Enhanced Design */}
            <div className="mt-6 sm:mt-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6 sm:p-8 shadow-md hover:shadow-lg transition-all duration-300">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                    <Icon name="Briefcase" size={28} color="white" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-lg sm:text-xl font-bold text-gray-900">
                      Vous êtes client pro ?
                    </p>
                    <p className="text-sm sm:text-base text-gray-600">
                      Accédez à votre espace professionnel dédié
                    </p>
                  </div>
                  <Link
                    to="/professional-client-login"
                    className="inline-flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 min-h-[48px] text-base sm:text-lg w-full sm:w-auto"
                  >
                    <Icon name="LogIn" size={20} />
                    <span>Connectez-vous ici</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}